import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, Sparkles, Infinity as InfIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { Spinner } from "@/components/ui-bits";
import DownloadLinks from "@/components/DownloadLinks";

export default function Plan() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/subscription/status");
      setData(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  // Enlace del email: /plan?pay=monthly|annual abre el pago directamente
  const location = useLocation();
  const payParam = new URLSearchParams(location.search).get("pay");
  const autoStarted = useRef(false);
  const alreadyPaid = data && data.plan === "pro" && !data.trial_active;
  useEffect(() => {
    if (!data || autoStarted.current || !["monthly", "annual"].includes(payParam) || alreadyPaid) return;
    autoStarted.current = true;
    startCheckout(payParam);
  }, [data, payParam, alreadyPaid]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTrial = async () => {
    setBusy(true); setMsg("");
    try {
      const { data: r } = await api.post("/subscription/trial/start");
      if (r.ok) {
        setMsg(lang === "en" ? "Trial started" : "Prueba iniciada");
        load();
      } else setMsg(r.reason === "trial_already_used" ? t("plan.trial_used") : "Error");
    } finally { setBusy(false); }
  };

  const startCheckout = async (priceType) => {
    setBusy(true); setMsg("");
    try {
      const { data: r } = await api.post("/billing/checkout", {
        price_type: priceType,
        origin_url: window.location.origin,
      });
      if (r.url) {
        window.location.href = r.url;
        return;
      }
    } catch (ex) {
      setMsg(ex?.response?.data?.detail || (lang === "en" ? "Error starting checkout" : "Error al iniciar el pago"));
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true); setMsg("");
    try {
      const { data: r } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/plan",
      });
      if (r.url) {
        window.location.href = r.url;
        return;
      }
    } catch (ex) {
      setMsg(ex?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const applyPromo = async () => {
    if (!code.trim()) return;
    setBusy(true); setMsg("");
    try {
      const { data: r } = await api.post("/subscription/promo/apply", { code });
      if (r.ok) setMsg(t("plan.promo.ok", { pct: r.discount_pct }));
      else setMsg(t("plan.promo.invalid"));
    } catch { setMsg(t("plan.promo.invalid")); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-6"><Spinner /></div>;

  const isPro = data.plan === "pro";
  const cap = (v) => v == null ? t("plan.unlimited") : String(v);

  // Título del plan según su estado real
  const planTitle = (() => {
    if (!isPro) return t("plan.free");
    if (data.trial_active) return t("plan.trial_title");
    if (data.billing_cycle === "annual") return t("plan.annual_title");
    if (data.billing_cycle === "monthly") return t("plan.monthly_title");
    return t("plan.pro"); // grandfathered u otros
  })();

  // Texto del contador de trial
  const trialDaysText = (() => {
    if (!data.trial_active) return null;
    const n = data.trial_days_left || 0;
    if (n <= 0) return t("plan.trial.ends_today");
    const key = n === 1 ? "plan.trial.days_left_one" : "plan.trial.days_left_many";
    return t(key).replace("{n}", n);
  })();

  return (
    <div className="px-5 pt-8 pb-24 sns-fade-up max-w-3xl mx-auto" data-testid="page-plan">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center">
          <ArrowLeft size={18} />
        </button>
        <h1 className="sns-title text-2xl flex-1">{t("plan.title")}</h1>
      </div>

      {payParam && alreadyPaid && (
        <div className="sns-card p-4 mb-4 !border-2 !border-emerald-300 !bg-emerald-50 text-sm text-emerald-900" data-testid="plan-already-paid">
          {lang === "en"
            ? "This account already has Pro active, so there is nothing to pay. The payment link is for managers whose trial has ended."
            : "Esta cuenta ya tiene Pro activo, así que no hay nada que pagar. El enlace de pago es para gerentes cuya prueba ha terminado: al entrar con su cuenta se abre el pago directamente."}
        </div>
      )}
      {busy && payParam && !alreadyPaid && (
        <div className="sns-card p-4 mb-4 text-sm text-[var(--sky-text)] !bg-[var(--sky)] font-semibold" data-testid="plan-opening-checkout">
          {lang === "en" ? "Opening secure payment…" : "Abriendo el pago seguro…"}
        </div>
      )}

      {/* Current plan card */}
      <div className={`sns-card p-6 mb-4 ${isPro ? "bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white" : ""}`} data-testid="plan-current">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-bold tracking-wider ${isPro ? "text-cyan-300" : "text-slate-400"}`}>
            {t("plan.current")}
          </span>
          {data.trial_active && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-400 text-emerald-900" data-testid="badge-trial">
              {t("plan.trial")}
            </span>
          )}
        </div>
        <div className={`sns-title text-4xl ${isPro ? "text-white" : ""}`} data-testid="plan-title">{planTitle}</div>
        {trialDaysText && (
          <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${
            (data.trial_days_left || 0) <= 2 ? "text-red-300" :
            (data.trial_days_left || 0) <= 7 ? "text-amber-300" : "text-cyan-200"
          }`} data-testid="trial-days-left">
            <span className="inline-block w-2 h-2 rounded-full bg-current animate-pulse"></span>
            {trialDaysText}
          </div>
        )}
        {!isPro && (
          <p className="text-slate-500 text-sm mt-2">
            {String(data.pricing.monthly_eur).replace(".", ",")}{t("plan.price.monthly")} · {data.pricing.yearly_eur.toFixed(2).replace(".", ",")}{t("plan.price.yearly")} + {lang === "en" ? "VAT" : "IVA"}
          </p>
        )}
      </div>

      {/* Usage grid */}
      <h3 className="text-xs font-bold text-slate-400 tracking-wider mb-3 px-2">{t("plan.usage")}</h3>
      <div className="sns-card p-5 mb-6 grid grid-cols-3 gap-4" data-testid="plan-usage">
        <UsageBox label={t("plan.properties")} used={data.usage.properties} cap={cap(data.limits.max_properties)} />
        <UsageBox label={t("plan.employees")} used={data.usage.employees} cap={cap(data.limits.max_employees)} />
        {data.limits.max_users != null && <UsageBox label={lang === "en" ? "Users" : "Usuarios"} used={data.usage.users || 0} cap={cap(data.limits.max_users)} />}
        <UsageBox label={t("plan.maintenance_month")} used={data.usage.maintenance_this_month} cap={cap(data.limits.max_maintenance_per_month)} />
      </div>

      {/* Upgrade card (only if free) */}
      {!isPro && (
        <div className="sns-card p-6 mb-6 border-2 border-[var(--coral)]" data-testid="plan-upgrade-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-[var(--coral)]" />
            <h3 className="sns-title text-xl">{t("plan.upgrade.title")}</h3>
          </div>
          <p className="text-slate-600 text-sm mb-4">{t("plan.upgrade.desc")}</p>
          <ul className="space-y-2 mb-5 text-sm">
            <FeatureRow>Hasta 50 propiedades y embarcaciones</FeatureRow>
            <FeatureRow>50 usuarios · 30 empleados activos a la vez</FeatureRow>
            <FeatureRow>Trabajos sin límite</FeatureRow>
            <FeatureRow>Integración con Holded (presupuestos)</FeatureRow>
            <FeatureRow>Notificaciones push · Multi-idioma</FeatureRow>
            <FeatureRow>Exportaciones e informes avanzados</FeatureRow>
          </ul>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => startCheckout("monthly")}
              disabled={busy}
              className="sns-btn sns-btn-accent flex-1 py-3 disabled:opacity-50"
              data-testid="btn-upgrade-monthly"
            >
              {busy ? "..." : (lang === "en" ? "Monthly · 45,99€/mo + VAT" : "Mensual · 45,99€/mes + IVA")}
            </button>
            <button
              onClick={() => startCheckout("annual")}
              disabled={busy}
              className="sns-btn sns-btn-ghost bg-white border-2 border-[var(--coral)] text-[var(--coral)] flex-1 py-3 disabled:opacity-50"
              data-testid="btn-upgrade-annual"
            >
              {lang === "en" ? "Annual · 441,50€ + VAT (20% off)" : "Anual · 441,50€ + IVA (20 % dto.)"}
            </button>
          </div>

          {/* Promo code */}
          <div className="mt-5 pt-5 border-t border-[var(--border)]">
            <label className="text-xs font-semibold text-slate-600 mb-2 block">{t("plan.promo")}</label>
            <div className="flex gap-2">
              <input
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="sns-input flex-1"
                placeholder={t("plan.promo.placeholder")}
                data-testid="input-promo-code"
              />
              <button
                onClick={applyPromo}
                disabled={busy || !code.trim()}
                className="sns-btn sns-btn-ghost bg-slate-100 disabled:opacity-50"
                data-testid="btn-apply-promo"
              >
                {t("plan.promo.apply")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage subscription button when Pro */}
      {isPro && !data.grandfathered && (
        <div className="sns-card p-5 mb-6" data-testid="plan-manage-card">
          <p className="text-slate-600 text-sm mb-3">
            {lang === "en"
              ? "Manage your subscription, payment method, invoices or cancel."
              : "Gestiona tu suscripción, método de pago, facturas o cancela."}
          </p>
          <button
            onClick={openPortal}
            disabled={busy}
            className="sns-btn sns-btn-ghost bg-slate-100 w-full py-3 disabled:opacity-50"
            data-testid="btn-manage-subscription"
          >
            {busy ? "..." : (lang === "en" ? "Manage subscription" : "Gestionar suscripción")}
          </button>
        </div>
      )}

      {msg && <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3" data-testid="plan-msg">{msg}</p>}

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mt-6">
        <div className="text-xs font-bold tracking-wider text-slate-500 mb-3 text-center">
          {lang === "en" ? "AVAILABLE EVERYWHERE" : "DISPONIBLE DONDE QUIERAS"}
        </div>
        <DownloadLinks lang={lang} compact />
      </div>
    </div>
  );
}

const UsageBox = ({ label, used, cap }) => (
  <div>
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    <div className="sns-title text-2xl">
      {used}<span className="text-slate-300 text-lg"> / {cap === "Ilimitado" || cap === "Unlimited" ? <InfIcon size={20} className="inline" /> : cap}</span>
    </div>
  </div>
);

const FeatureRow = ({ children }) => (
  <li className="flex items-start gap-2 text-slate-700">
    <Check size={18} className="text-emerald-500 shrink-0 mt-0.5" />
    <span>{children}</span>
  </li>
);
